/**
 * Client-side image resize + WebP (JPEG fallback) compression for R2 uploads.
 */

import {
  ACCEPTED_IMAGE_MIME,
  MEDIA_KIND_CONFIG,
  assertAcceptedImageFile,
} from "./mediaTypes";

const MIN_QUALITY = 0.45;

/**
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} mime
 * @param {number} quality
 * @returns {Promise<Blob>}
 */
function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not compress image"));
      },
      mime,
      quality
    );
  });
}

/**
 * Detect WebP encode support once.
 * @returns {Promise<boolean>}
 */
let webpSupportPromise = null;
export function supportsWebpEncoding() {
  if (webpSupportPromise) return webpSupportPromise;
  webpSupportPromise = (async () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 2;
      canvas.height = 2;
      const blob = await canvasToBlob(canvas, "image/webp", 0.8);
      return Boolean(blob && blob.type === "image/webp");
    } catch {
      return false;
    }
  })();
  return webpSupportPromise;
}

/**
 * Compress for a media kind. Prefers WebP; falls back to JPEG.
 * @param {File} file
 * @param {{
 *   kind?: string,
 *   maxEdge?: number,
 *   targetMaxBytes?: number,
 *   onProgress?: (phase: 'compressing'|'ready', detail?: object) => void,
 * }} [opts]
 * @returns {Promise<{ blob: Blob, mime: string, width: number, height: number }>}
 */
export async function compressImageForMedia(file, opts = {}) {
  assertAcceptedImageFile(file);
  const kindCfg = opts.kind ? MEDIA_KIND_CONFIG[opts.kind] : null;
  const maxEdge = opts.maxEdge ?? kindCfg?.maxEdge ?? 512;
  const targetMaxBytes = opts.targetMaxBytes ?? kindCfg?.targetMaxBytes ?? 200_000;
  opts.onProgress?.("compressing");

  const img = await loadImageFromFile(file);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process image");

  const preferWebp = await supportsWebpEncoding();
  const mime = preferWebp ? "image/webp" : "image/jpeg";

  let edge = maxEdge;
  /** @type {Blob | null} */
  let best = null;

  while (edge >= 160) {
    const scale = Math.min(1, edge / Math.max(img.width, img.height, 1));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);
    // White fill so PNG transparency becomes opaque (better for avatars).
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    for (let q = 0.85; q >= MIN_QUALITY; q -= 0.1) {
      const blob = await canvasToBlob(canvas, mime, q);
      if (blob.size <= targetMaxBytes) {
        opts.onProgress?.("ready", { bytes: blob.size, mime, width: w, height: h });
        return { blob, mime, width: w, height: h };
      }
      if (!best || blob.size < best.size) best = blob;
    }
    edge = Math.floor(edge * 0.75);
  }

  if (best && best.size <= targetMaxBytes * 1.25) {
    opts.onProgress?.("ready", { bytes: best.size, mime });
    return {
      blob: best,
      mime,
      width: canvas.width,
      height: canvas.height,
    };
  }
  throw new Error("Image is still too large after compression — try a smaller photo");
}

/**
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Could not read image"));
    };
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(blob);
  });
}

export { ACCEPTED_IMAGE_MIME };
